'use client';

import { useParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from '@sola/ark-core/utils/hooks/useRouter.ts';
import { useChildAvailability, useChildDetails } from '@sola/my-sdk/children/hooks.ts';
import { getChildBioPathWithSubAccepted, getDepartureSubAlternatePath, getDepartureSubPath, knownMyUrls } from '@sola/my-sdk/constants.ts';
import { useDepartureSubUpdate } from '@sola/my-sdk/departures/hooks.ts';
import { DepartureSubUnavailableError } from '@sola/my-sdk/departures/requests.ts';
import type { ChildAvailabilityItem, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import type { DepartureSubUpdateRequest } from '@sola/schema/dotcom/proxy-api/departures.ts';
import type { GraphQLDepartureChild, GraphQLDepartureSub } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useDepartureSummary } from '@sola/supporter-sdk/hooks/commitments.ts';
import { saveDepartureSubConfirmation } from '../../utils/sub-acceptance.ts';
export type DepartureSubDecisionError = 'generic' | 'unavailable';
export interface DepartureSubPageContextValue {
  departure: {
    autoAcceptDate?: string | null;
    availability?: ChildAvailabilityItem;
    departedChildGlobalId?: string;
    departingChild?: ChildDetails;
    displayedChild?: ChildDetails;
    displayedChildGlobalId?: string;
    isAlternateSelection: boolean;
    offeredChild?: GraphQLDepartureChild;
    offeredChildGlobalId?: string;
  };
  decision: {
    accept: () => Promise<void>;
    error?: DepartureSubDecisionError;
    isPending: boolean;
  };
  hasError: boolean;
  navigation: {
    getAlternateChildPath: (childGlobalId: string) => string;
    offeredChildPath: string;
    selectAlternateChild: (childGlobalId: string) => void;
  };
}
interface DepartureSubPageController {
  contextValue: DepartureSubPageContextValue;
  isPagePending: boolean;
}
interface DepartureSubSelection {
  displayedChildGlobalId?: string;
  isAlternateSelection: boolean;
  offeredChildGlobalId?: string;
}
export const getDepartureSubSelection = ({
  offeredSub,
  replacementChildGlobalId
}: {
  offeredSub?: GraphQLDepartureSub;
  replacementChildGlobalId?: string;
}): DepartureSubSelection => {
  const offeredChildGlobalId = offeredSub?.child.globalId;
  const isAlternateSelection = !!replacementChildGlobalId && replacementChildGlobalId !== offeredChildGlobalId;
  return {
    displayedChildGlobalId: isAlternateSelection ? replacementChildGlobalId : offeredChildGlobalId,
    isAlternateSelection,
    offeredChildGlobalId
  };
};
export const getActiveDepartureSub = ({
  offeredSub,
  previousOfferedSub,
  isDecisionTransition
}: {
  offeredSub?: GraphQLDepartureSub;
  previousOfferedSub?: GraphQLDepartureSub;
  isDecisionTransition: boolean;
}): GraphQLDepartureSub | undefined => offeredSub ?? (isDecisionTransition ? previousOfferedSub : undefined);

/**
 * Resolves where selecting a child in the search sheet should navigate.
 * Re-selecting the child that is already displayed returns `undefined` so the
 * caller can simply close the sheet instead of navigating to the same route
 * (which would otherwise be a no-op and leave the sheet open). Only a genuinely
 * different child produces an alternate path to push.
 */
export const getAlternateChildNavigationPath = ({
  departedChildGlobalId,
  displayedChildGlobalId,
  selectedChildGlobalId
}: {
  departedChildGlobalId?: string;
  displayedChildGlobalId?: string;
  selectedChildGlobalId: string;
}): string | undefined => !departedChildGlobalId || selectedChildGlobalId === displayedChildGlobalId ? undefined : getDepartureSubAlternatePath(departedChildGlobalId, selectedChildGlobalId);
export const getDepartureSubDecisionPayload = ({
  displayedChildGlobalId,
  offeredChildGlobalId
}: {
  displayedChildGlobalId: string;
  offeredChildGlobalId: string;
}): DepartureSubUpdateRequest => displayedChildGlobalId === offeredChildGlobalId ? {
  acceptOfferedChild: true
} : {
  acceptOfferedChild: false,
  replacementChildGlobalId: displayedChildGlobalId
};
export const useDepartureSubPageController = (): DepartureSubPageController => {
  const params = useParams<{
    globalId: string;
    replacementChildGlobalId?: string;
  }>();
  const departedChildGlobalId = params?.globalId;
  const replacementChildGlobalId = params?.replacementChildGlobalId;
  const {
    router
  } = useRouter();
  const departureSummaryQuery = useDepartureSummary();
  const departure = departureSummaryQuery.departureSummary?.departures.find(({
    departedChild
  }) => departedChild.globalId === departedChildGlobalId);
  const currentOfferedSub = departure?.sub?.shouldDisplay === true && departure.sub.completed === false ? departure.sub : undefined;
  const decisionMutation = useDepartureSubUpdate(departedChildGlobalId);
  const previousOfferedSubRef = useRef<GraphQLDepartureSub | undefined>(undefined);
  if (currentOfferedSub) {
    previousOfferedSubRef.current = currentOfferedSub;
  }
  const offeredSub = getActiveDepartureSub({
    offeredSub: currentOfferedSub,
    previousOfferedSub: previousOfferedSubRef.current,
    isDecisionTransition: decisionMutation.isPending || decisionMutation.isSuccess
  });
  const {
    displayedChildGlobalId,
    isAlternateSelection,
    offeredChildGlobalId
  } = getDepartureSubSelection({
    offeredSub,
    replacementChildGlobalId
  });
  const displayedChildDetailsQuery = useChildDetails(displayedChildGlobalId);
  const displayedChildAvailabilityQuery = useChildAvailability(displayedChildGlobalId);
  const departingChildDetailsQuery = useChildDetails(offeredSub ? departedChildGlobalId : undefined);
  const [decisionError, setDecisionError] = useState<DepartureSubDecisionError>();
  const isDeparturePending = departureSummaryQuery.isPending;
  const hasError = departureSummaryQuery.isError || displayedChildDetailsQuery.isError || displayedChildAvailabilityQuery.isError || departingChildDetailsQuery.isError;
  const shouldRedirectFromUnavailableSub = !!departedChildGlobalId && !isDeparturePending && !departureSummaryQuery.isError && !offeredSub && !decisionMutation.isPending && !decisionMutation.isSuccess;
  useEffect(() => {
    if (shouldRedirectFromUnavailableSub) {
      router.replace(knownMyUrls.sponsorshipsBase);
    }
  }, [shouldRedirectFromUnavailableSub, router]);
  const accept = async (): Promise<void> => {
    if (!departedChildGlobalId || !offeredChildGlobalId || !displayedChildGlobalId || decisionMutation.isPending) {
      return;
    }
    setDecisionError(undefined);
    try {
      await decisionMutation.mutateAsync(getDepartureSubDecisionPayload({
        displayedChildGlobalId,
        offeredChildGlobalId
      }));
      saveDepartureSubConfirmation({
        acceptedChildGlobalId: displayedChildGlobalId
      });
      router.replace(getChildBioPathWithSubAccepted(displayedChildGlobalId));
    } catch (error) {
      setDecisionError(error instanceof DepartureSubUnavailableError ? 'unavailable' : 'generic');
      throw error;
    }
  };
  const isChildDataPending = !!offeredSub && (displayedChildDetailsQuery.isPending || displayedChildAvailabilityQuery.isPending || departingChildDetailsQuery.isPending);
  const isPagePending = !hasError && (isDeparturePending || isChildDataPending || shouldRedirectFromUnavailableSub);
  return {
    contextValue: {
      departure: {
        autoAcceptDate: departure?.autoAcceptDate,
        availability: displayedChildAvailabilityQuery.data,
        departedChildGlobalId,
        departingChild: departingChildDetailsQuery.data,
        displayedChild: displayedChildDetailsQuery.data,
        displayedChildGlobalId,
        isAlternateSelection,
        offeredChild: offeredSub?.child,
        offeredChildGlobalId
      },
      decision: {
        accept,
        error: decisionError,
        isPending: decisionMutation.isPending
      },
      hasError,
      navigation: {
        getAlternateChildPath: (childGlobalId: string): string => departedChildGlobalId ? getDepartureSubAlternatePath(departedChildGlobalId, childGlobalId) : knownMyUrls.sponsorshipsBase,
        offeredChildPath: departedChildGlobalId ? getDepartureSubPath(departedChildGlobalId) : knownMyUrls.sponsorshipsBase,
        selectAlternateChild: (childGlobalId_0: string): void => {
          const alternatePath = getAlternateChildNavigationPath({
            departedChildGlobalId,
            displayedChildGlobalId,
            selectedChildGlobalId: childGlobalId_0
          });
          // A different child navigates to its match page; re-selecting the
          // displayed child yields no path, so the caller just closes the sheet.
          if (alternatePath) {
            router.push(alternatePath);
          }
        }
      }
    },
    isPagePending
  };
};